import type { InputHTMLAttributes, ReactNode } from "react";

import { cn } from "../lib/cn.js";

/**
 * A labelled checkbox.
 *
 * ==========================================================================================
 * THE APPLICATION'S FIRST ONE, WHICH IS WHY IT IS HERE AND NOT IN A DOMAIN PACKAGE
 * ==========================================================================================
 *
 * Before feature 018 there was no checkbox anywhere in GLADIS — not in this package, not in any domain
 * `ui/`, not in `apps/app`. The activity log's "Hide system events" toggle is the first, and wherever the
 * first one lands is where the second gets copied from. A checkbox knows nothing about households or
 * activity entries, so it belongs in the design system; `StatCard` arrived the same way in 009.
 *
 * ==========================================================================================
 * A NATIVE INPUT, STYLED
 * ==========================================================================================
 *
 * `appearance-none` plus a drawn tick rather than a `role="checkbox"` div. The native element brings the
 * label association, the space-bar activation, the focus order and the announced state for free, and every
 * one of those is something a div reimplements badly. The visual is the only part worth replacing.
 *
 * The label WRAPS the input rather than pointing at it with `htmlFor`. Wrapping cannot desynchronise —
 * there is no id to get wrong, and no way to ship a label attached to nothing.
 *
 * Every colour is a declared token. `token-ownership.test.ts` enforces that after `text-danger` compiled
 * to nothing in twelve places in 009: a Tailwind utility naming an undeclared token is not an error, it is
 * silence, and the control renders unstyled.
 *
 * `rounded-check` AND NOT `rounded-control`. `--radius-control` is 8px, sized for a 36px-tall button; on a
 * 16px box that is half the width and the result is a circle — indistinguishable from a radio button, which
 * says "pick one of these" rather than "this is on". The first version of this component shipped that, and
 * it was caught by looking at the page rather than by any test.
 */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** The visible label. Required — an unlabelled checkbox is a control nobody can name. */
  label: ReactNode;
}

export function Checkbox({ label, className, ...rest }: CheckboxProps): ReactNode {
  return (
    <label
      className={cn(
        "inline-flex cursor-pointer items-center gap-2 text-sm text-ink select-none",
        rest.disabled === true && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <input
        type="checkbox"
        className={cn(
          "size-4 shrink-0 cursor-pointer appearance-none rounded-check",
          "border border-input bg-input/30",
          "transition-colors outline-none",
          "checked:border-primary checked:bg-primary",
          // The tick, drawn as a background image so no pseudo-element or child span is needed. White
          // rather than a token because it sits on `bg-primary`, whose foreground token is exactly this.
          "checked:bg-[url('data:image/svg+xml,%3Csvg%20viewBox=%220%200%2016%2016%22%20fill=%22none%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath%20d=%22M3.5%208.5l3%203%206-6%22%20stroke=%22white%22%20stroke-width=%222%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22/%3E%3C/svg%3E')]",
          "checked:bg-center checked:bg-no-repeat",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          "disabled:cursor-not-allowed",
        )}
        {...rest}
      />
      {label}
    </label>
  );
}
